import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, FolderOpen, Plus, Trash2, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, PageHeader, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

const MODULES = ["general", "company", "party", "employee", "invoice", "proforma",
  "purchase_order", "purchase_invoice", "work_order", "asset", "expense", "payment"];

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Documents() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [module, setModule] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", module: "general", record_id: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    const qs = module !== "all" ? `?module=${module}` : "";
    api.get(`/documents${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId, module]); // eslint-disable-line

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/upload?module=${form.module}&record_id=${encodeURIComponent(form.record_id)}&title=${encodeURIComponent(form.title || file.name)}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDialog(false); setFile(null); setForm({ title: "", module: "general", record_id: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.target = "_blank";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/delete`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="documents-page">
      <PageHeader title="Document Manager" testId="documents-title" sub="Central, permission-controlled file store per company">
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger data-testid="documents-module-filter" className="w-44 bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modules</SelectItem>
            {MODULES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="upload-doc-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> Upload</Button>
      </PageHeader>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="documents-empty" title="No documents yet"
            body="Upload contracts, KYC files, bills and certificates — PDF, images, Office docs up to 10 MB." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Document</th><th className="p-3">Module</th><th className="p-3">Size</th>
                <th className="p-3">Uploaded By</th><th className="p-3">Date</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <span className="rounded-md bg-slate-100 dark:bg-slate-800 p-1.5"><FileText className="h-4 w-4 text-slate-500" /></span>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[240px]">{d.title}</p>
                          <p className="text-xs text-slate-400 font-mono truncate">{d.original_filename}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3"><Badge variant="secondary" className="capitalize text-[10px]">{d.module.replace(/_/g, " ")}</Badge></td>
                    <td className="p-3 font-mono text-xs text-slate-500">{fmtSize(d.size)}</td>
                    <td className="p-3 text-slate-500">{d.uploaded_by_name}</td>
                    <td className="p-3 text-slate-500">{fmtDate(d.created_at)}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" data-testid={`doc-download-${d.id}`} onClick={() => download(d)}><Download className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="upload-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FolderOpen className="h-5 w-5 text-amber-500" /> Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File * (PDF, JPG, PNG, DOCX, XLSX, CSV — max 10 MB)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" data-testid="doc-file-input"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.csv,.txt,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <Button variant="outline" data-testid="doc-choose-file" onClick={() => fileRef.current?.click()}>
                  <Upload className="mr-1.5 h-4 w-4" /> Choose file
                </Button>
                {file && <span className="text-xs text-slate-500 truncate">{file.name} ({fmtSize(file.size)})</span>}
              </div>
            </div>
            <div><Label>Title</Label><Input data-testid="doc-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Defaults to file name" /></div>
            <div><Label>Module</Label>
              <Select value={form.module} onValueChange={(v) => setForm((f) => ({ ...f, module: v }))}>
                <SelectTrigger data-testid="doc-module" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{MODULES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Linked Record ID (optional)</Label><Input className="mt-1.5 font-mono text-xs" value={form.record_id} onChange={(e) => setForm((f) => ({ ...f, record_id: e.target.value }))} placeholder="e.g. invoice id" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="doc-upload-save-btn" onClick={upload} disabled={busy || !file}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Uploading..." : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
